import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { Search } from '@mui/icons-material';
import { useDistance } from 'new/hooks/useDistance';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import DateUtils from 'js/lib/DateUtils';
import { Table } from 'new/elements/Table';
import {
  getCredentialsGroupCredentials,
  getCredentialsGroupCredentialsListing,
  loadDataCredentials,
  setTotalItems,
  setTotalPages,
} from 'js/actions';
import useAppDispatch from 'js/hooks/useAppDispatch';
import {
  GET_CREDENTIALS_GROUP_CREDENTIALS_REQUEST,
  GET_ALL_CREDENTIALS_GROUP_CREDENTIALS_REQUEST,
} from 'js/constants/actionTypes';
import generateColumns from './generateColumns';
import styles from './CredentialGroupOverviewTab.module.scss';
import useCredentialsGroupAdapter from 'new/state/credentialsGroup/adapter/useCredentialsGroupAdapter';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import {
  SelectOption,
  SelectInput,
  redesignStyles,
} from 'js/components/elements/rselect/ReactSelectUtils';
import { Button } from 'new/elements/Button';
import CardsListPageNumbersContainer from 'js/components/elements/cards/CardsListPageNumbersContainer';
import { RemoveIconButton } from 'new/elements/Button/RemoveIconButton';
import { useFilteredData } from 'new/hooks/useFilteredData';
import { TextField } from 'new/elements/TextField';
import useCredentialsGroup from 'new/state/credentialsGroup/repository/useCredentialsGroup';
import { Credential } from '@nexla/@types';
import UserConfirmation from 'js/components/route_utils/UserConfirmation';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { RESOURCE_TYPE_CREDENTIALS } from 'js/constants/resourceTypes';
import { useResourceMeta } from 'new/hooks/adapters/useResourceMeta';

const selectStyles = {
  container: (styles) => ({
    ...styles,
    width: 400,
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  }),
  valueContainer: (styles) => ({
    ...styles,
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    flexWrap: 'nowrap',
    display: 'flex',
    gap: '4px',
  }),
  multiValue: (styles) => ({
    ...styles,
    flexShrink: 0,
    minWidth: 'fit-content',
    margin: 0,
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    whiteSpace: 'nowrap',
  }),
  control: (styles) => ({
    ...styles,
    boxShadow: 'none',
  }),
};

export default function CredentialGroupOverviewTab({ data }) {
  const dispatch = useAppDispatch();
  const { updateCredentialGroupCredentials } = useCredentialsGroupAdapter();
  const isLoading = useIsLoading(
    GET_CREDENTIALS_GROUP_CREDENTIALS_REQUEST,
    GET_ALL_CREDENTIALS_GROUP_CREDENTIALS_REQUEST,
  );
  const containerRef = useRef();
  const distance = useDistance(containerRef, 400);
  const groupId = data.id;
  const primaryCredentialId = data?.primaryCredentialId;
  const connectionInfo = useResourceMeta(RESOURCE_TYPE_CREDENTIALS, primaryCredentialId);
  const { availableCredentialsOfType, allCredentials, credentialsListing } = useCredentialsGroup(
    data,
    connectionInfo,
  );
  const { currentPage } = useSelector((state: any) => state.app.listing);
  const [selectedCredentials, setSelectedCredentials] = useState([]);
  const [credentialToRemove, setCredentialToRemove] = useState(null);
  const createdDate = DateUtils.convertDateStringToFormat(data.created_at);
  const lastUpdate = DateUtils.convertDateStringToFormat(data.updated_at);
  const credentialsOptions = availableCredentialsOfType.map((cred: Credential) => ({
    value: cred.id,
    label: `${cred.name} (ID: ${cred.id})` || '(Untitled)',
  }));

  const selectedCredentialCount =
    selectedCredentials?.length === 0 ? '' : selectedCredentials?.length;

  const { filteredData: filteredCredentials, onSearchChange } = useFilteredData(
    allCredentials,
    'name',
  );

  // temporary NEX-16090: Search box workaround - calculate pagination for filtered results
  // to be replaced with credentialsListing when search enabled
  const itemsPerPage = 20;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFilteredCredentials = filteredCredentials.slice(startIndex, endIndex);

  useEffect(() => {
    dispatch(getCredentialsGroupCredentials(groupId)).catch((error) => {
      console.error('Error fetching all group credentials:', error);
    });
    dispatch(loadDataCredentials(ACLQueryModeTypes.MODE_COLLABORATOR.key));
  }, [groupId]);

  useEffect(() => {
    // only update pagination if we're using the filtered results - temp until api is fixed
    if (filteredCredentials.length > 0) {
      dispatch(setTotalItems(filteredCredentials.length));
      dispatch(setTotalPages(Math.ceil(filteredCredentials.length / itemsPerPage)));
    } else {
      // otherwise use the server-side pagination
      dispatch(getCredentialsGroupCredentialsListing(groupId, currentPage))
        .then((response) => {
          dispatch(setTotalPages(response.totalPages));
          dispatch(setTotalItems(response.totalItems));
        })
        .catch((error) => {
          console.error('Error fetching paginated group credentials:', error);
        });
    }
  }, [groupId, currentPage, filteredCredentials.length]);

  const handleDeleteCredentialFromGroup = (credentialId: number) => {
    const credential = allCredentials.find((cred) => cred.id === credentialId);
    setCredentialToRemove({ id: credentialId, name: credential?.name });
  };

  const confirmDeleteCredential = () => {
    if (credentialToRemove) {
      const newCredentialIds = allCredentials
        .map((cred) => cred.id)
        .filter((id) => id !== credentialToRemove.id);

      updateCredentialGroupCredentials(groupId, newCredentialIds);
      setCredentialToRemove(null);
    }
  };

  const cancelDeleteCredential = () => setCredentialToRemove(null);

  const handleAddCredentialsToGroup = (selectedCredentials: any[]) => {
    const addedCredentialIds = selectedCredentials.map((cred) => cred.value);
    const existingCredentialIds = allCredentials.map((cred) => cred.id);
    const newCredentialIds = [...addedCredentialIds, ...existingCredentialIds];

    updateCredentialGroupCredentials(groupId, newCredentialIds);
    setSelectedCredentials([]);
  };

  const handleChangeSelectedCredential = (event: any) => {
    setSelectedCredentials(event);
  };

  const columns = generateColumns();

  const rowEndRender = (row) => {
    if (!row) {
      return null;
    } else if (row.id === primaryCredentialId) {
      return (
        <RemoveIconButton
          tooltipText="Primary Credential cannot be removed"
          disabled={true}
          onClickRemove={() => {}}
        />
      );
    }
    return (
      <RemoveIconButton
        onClickRemove={() => {
          handleDeleteCredentialFromGroup(row.id);
        }}
        tooltipText="Remove Credential"
      />
    );
  };

  const sortedSelectStyles = useMemo(() => {
    return {
      ...redesignStyles,
      ...selectStyles,
    };
  }, [redesignStyles, selectStyles]);

  const menuPortalTarget = typeof window !== 'undefined' ? document.body : undefined;

  return (
    <div className={styles.overviewContainer} ref={containerRef}>
      {credentialToRemove && (
        <UserConfirmation
          title="Remove Credential"
          message={`Are you sure you want to remove the credential "${credentialToRemove.name}" (ID: ${credentialToRemove.id}) from this group?`}
          okButtonLabel="Remove"
          okButtonTheme="delete"
          onCancel={cancelDeleteCredential}
          onConfirm={confirmDeleteCredential}
        />
      )}
      <div className="tab-content data-map-overview-tab">
        <section>
          <div className="detail-row">
            <div className="detail-col">
              <div className="detail-key">Credential Group ID</div>
              <div className="detail-value">{groupId}</div>
            </div>
          </div>
          <div className="detail-row">
            <div className="detail-col">
              <div className="detail-key">Created On</div>
              <div className="detail-value">{createdDate}</div>
            </div>
          </div>
          <div className="detail-row">
            <div className="detail-col">
              <div className="detail-key">Last Updated On</div>
              <div className="detail-value">{lastUpdate}</div>
            </div>
          </div>
        </section>
        <hr />
        <div className={styles.credentialsContainer}>
          <Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              padding="16px"
            >
              <Typography variant="subtitle1">Group Credentials: </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <SortedSelect
                  menuPortalTarget={menuPortalTarget}
                  variant="default"
                  placeholder="Select Credential..."
                  value={selectedCredentials}
                  isSearchable
                  isMulti
                  closeMenuOnSelect={false}
                  isLoading={isLoading}
                  backspaceRemovesValue
                  styles={sortedSelectStyles}
                  options={credentialsOptions}
                  onChange={handleChangeSelectedCredential}
                  components={{
                    Input: SelectInput,
                    Option: SelectOption,
                  }}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={!selectedCredentials.length}
                  onClick={() => handleAddCredentialsToGroup(selectedCredentials)}
                >
                  Add {selectedCredentialCount} Credential
                </Button>
              </Stack>
            </Stack>
            <Stack gap="4px">
              <div className={styles.controlsContainer}>
                <TextField
                  startAdornment={<Search />}
                  onChange={onSearchChange}
                  placeholder="Type to filter by name"
                  className={styles.searchBar}
                />
                <CardsListPageNumbersContainer isLoading={isLoading} />
              </div>
              <div className={styles.content}>
                <Table
                  height={distance}
                  isSkeletonLoadingMode
                  loading={isLoading}
                  parentScrollRef={containerRef}
                  columns={columns}
                  rows={paginatedFilteredCredentials}
                  rowEndRender={rowEndRender}
                />
              </div>
            </Stack>
          </Stack>
        </div>
      </div>
    </div>
  );
}
